"use client"

/**
 * BandScale — a reading as a position on the scale it is judged against.
 *
 * Shared by the question preview sheet and the Blueprint tab so faculty see
 * one graph dialect for Usage / Difficulty / Discrimination.
 */

import * as React from "react"
import {
  Bar,
  BarChart,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts"

import {
  ChartContainer,
  ChartTooltip,
  chartTooltipKeyboardSyncProps,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart"
import {
  D_INDEX_ADEQUATE_MIN,
  D_INDEX_STRONG_MIN,
} from "@/lib/discrimination"
import { PBI_ADEQUATE_MIN, PBI_STRONG_MIN } from "@/lib/pbi-bands"
import { REUSE_OVERUSED_AT } from "@/lib/question-reuse"
import { cn } from "@/lib/utils"
import { type Band } from "@/components/assessment-structure/band-scale-shared"

/**
 * 0.60 rather than 1.00. Discrimination is bounded at 1 in theory and lives
 * under 0.6 in practice, so a full axis would squash every real reading into
 * the left third and make the bands unreadable.
 */
export const DISCRIMINATION_SCALE_MAX = 0.6

export const USAGE_SCALE_MAX = 6

interface ScaleTone {
  chip: string
  fill: string
  marker: string
  swatch: string
  label: string
}

const MARKER_RING = "[&_.recharts-reference-dot-dot]:!fill-background"

const SCALE_TONE: Record<Band["tone"], ScaleTone> = {
  neutral: {
    chip: "bg-brand-1/10 text-brand-1",
    fill: "fill-brand-1",
    marker: `${MARKER_RING} [&_.recharts-reference-dot-dot]:!stroke-brand-1`,
    swatch: "bg-brand-1",
    label: "text-brand-1",
  },
  success: {
    chip: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    fill: "fill-emerald-600 dark:fill-emerald-500",
    marker: `${MARKER_RING} [&_.recharts-reference-dot-dot]:!stroke-emerald-600 dark:[&_.recharts-reference-dot-dot]:!stroke-emerald-500`,
    swatch: "bg-emerald-600 dark:bg-emerald-500",
    label: "text-emerald-700 dark:text-emerald-400",
  },
  danger: {
    chip: "bg-destructive/10 text-destructive",
    fill: "fill-destructive",
    marker: `${MARKER_RING} [&_.recharts-reference-dot-dot]:!stroke-destructive`,
    swatch: "bg-destructive",
    label: "text-destructive",
  },
  warning: {
    chip: "bg-[var(--chip-4)]/15 text-foreground",
    fill: "fill-[var(--chip-4)]",
    marker: `${MARKER_RING} [&_.recharts-reference-dot-dot]:!stroke-[var(--chip-4)]`,
    swatch: "bg-[var(--chip-4)]",
    label: "text-foreground",
  },
}

const SCALE_ROW = "scale"
const MARKER_GUTTER = 7

export interface BandScaleProps {
  icon: string
  label: string
  badge?: React.ReactNode
  bands: readonly Band[]
  max: number
  /** Marker on the axis — typically a single reading or a set mean. */
  value: number | null
  /**
   * Optional spread across the set (e.g. section min–max). Drawn as a solid
   * bar under the average marker. Omit for single-question scales.
   */
  rangeLow?: number | null
  rangeHigh?: number | null
  reading: string
  aside?: string
  format: (value: number) => string
  /**
   * When set, band labels under the track are buttons that scope the pool
   * (Blueprint) or otherwise act on that slice.
   */
  onBandSelect?: (band: Band, index: number) => void
  className?: string
}

export function BandScale({
  icon,
  label,
  badge,
  bands,
  max,
  value,
  rangeLow = null,
  rangeHigh = null,
  reading,
  aside,
  format,
  onBandSelect,
  className,
}: BandScaleProps) {
  const [focused, setFocused] = React.useState(false)

  const activeIndex =
    value === null
      ? -1
      : bands.reduce(
          (found, band, index) => (value >= band.from ? index : found),
          0
        )

  const hasSpread =
    rangeLow != null &&
    rangeHigh != null &&
    Number.isFinite(rangeLow) &&
    Number.isFinite(rangeHigh)

  const spreadLo = hasSpread
    ? Math.min(max, Math.max(0, Math.min(rangeLow!, rangeHigh!)))
    : null
  const spreadHi = hasSpread
    ? Math.min(max, Math.max(0, Math.max(rangeLow!, rangeHigh!)))
    : null
  // Hairline when every value lands on the same point so the range still reads.
  const spreadPad =
    spreadLo != null && spreadHi != null && spreadHi - spreadLo < max * 0.02
      ? max * 0.015
      : 0

  const segments = bands.map((band, index) => {
    const next = bands[index + 1]
    const to = next ? next.from : max
    return {
      band,
      key: `band${index}`,
      width: (to - band.from) / max,
      span: to - band.from,
      range:
        index === 0
          ? `below ${format(to)}`
          : next
            ? `${format(band.from)} to ${format(to)}`
            : `${format(band.from)} and above`,
    }
  })

  const description = segments
    .map(({ band, range }) => `${band.label} ${range}`)
    .join(", ")

  const tone = SCALE_TONE[activeIndex < 0 ? "neutral" : bands[activeIndex].tone]

  const spreadPhrase =
    spreadLo != null && spreadHi != null
      ? spreadLo === spreadHi
        ? `range ${format(spreadLo)}`
        : `range ${format(spreadLo)} to ${format(spreadHi)}`
      : null

  const summary =
    value === null
      ? `${label}: no reading. Scale: ${description}.`
      : `${label}: ${reading}${aside ? `, ${aside}` : ""}${
          spreadPhrase ? `, ${spreadPhrase}` : ""
        }, which is ${bands[activeIndex].label}. Scale: ${description}.`

  const config: ChartConfig = Object.fromEntries(
    segments.map(({ band, key }) => [key, { label: band.label }])
  )

  const chartRow: Record<string, string | number> = { row: SCALE_ROW }
  for (const { key, span } of segments) chartRow[key] = span

  const sync = chartTooltipKeyboardSyncProps(focused ? 0 : null)

  return (
    <div className={cn("min-w-0 py-3.5 first:pt-0 last:pb-0", className)}>
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          aria-hidden
          className={cn(
            "grid size-8 shrink-0 place-items-center rounded-full",
            tone.chip
          )}
        >
          <i className={cn("fa-light text-sm", icon)} />
        </span>

        <span className="flex min-w-0 flex-1 items-center gap-2">
          <span className="min-w-0 truncate text-sm font-medium leading-5 text-foreground">
            {label}
          </span>
          {badge ? <span className="ml-auto shrink-0">{badge}</span> : null}
        </span>
      </div>

      <div
        role="img"
        tabIndex={0}
        aria-label={summary}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="mt-3 rounded-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChartContainer
          config={config}
          className={cn("aspect-auto h-6 w-full", tone.marker)}
        >
          <BarChart
            layout="vertical"
            data={[chartRow]}
            margin={{
              top: 0,
              right: MARKER_GUTTER,
              bottom: 0,
              left: MARKER_GUTTER,
            }}
          >
            <XAxis type="number" domain={[0, max]} hide />
            <YAxis type="category" dataKey="row" hide />

            <ChartTooltip
              key={sync.key}
              {...sync.props}
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={() => (
                    <span className="tabular-nums">
                      {reading}
                      {aside ? (
                        <span className="font-normal text-muted-foreground">
                          {" "}
                          <span aria-hidden>·</span> {aside}
                        </span>
                      ) : null}
                    </span>
                  )}
                  formatter={(_span, name) => {
                    const segment = segments.find((s) => s.key === name)
                    if (!segment) return null
                    const isActive = segments.indexOf(segment) === activeIndex

                    return (
                      <span className="flex w-full items-center gap-2">
                        <span
                          className={cn(
                            "size-2 shrink-0 rounded-[2px]",
                            tone.swatch,
                            isActive ? null : "opacity-30"
                          )}
                        />
                        <span
                          className={cn(
                            "flex-1",
                            isActive
                              ? "font-medium text-foreground"
                              : "text-muted-foreground"
                          )}
                        >
                          {segment.band.label}
                        </span>
                        <span
                          className={cn(
                            "tabular-nums",
                            isActive
                              ? "font-medium text-foreground"
                              : "text-muted-foreground"
                          )}
                        >
                          {segment.range}
                        </span>
                      </span>
                    )
                  }}
                />
              }
            />

            {segments.map(({ key }, index) => (
              <Bar
                key={key}
                dataKey={key}
                stackId="bands"
                barSize={8}
                className={tone.fill}
                // With a spread, the track stays faint and the range bar carries
                // the reading. Without one, the active band is the highlight.
                fillOpacity={
                  hasSpread ? 0.12 : index === activeIndex ? 1 : 0.15
                }
                radius={
                  index === 0
                    ? [4, 0, 0, 4]
                    : index === segments.length - 1
                      ? [0, 4, 4, 0]
                      : 0
                }
                isAnimationActive={false}
              />
            ))}

            {segments.slice(1).map(({ band }) => (
              <ReferenceLine
                key={band.label}
                x={band.from}
                stroke="var(--color-background)"
                strokeWidth={2}
              />
            ))}

            {spreadLo != null && spreadHi != null ? (
              <ReferenceArea
                x1={Math.max(0, spreadLo - spreadPad)}
                x2={Math.min(max, spreadHi + spreadPad)}
                y1={SCALE_ROW}
                y2={SCALE_ROW}
                ifOverflow="visible"
                shape={(props: {
                  x?: number
                  y?: number
                  width?: number
                  height?: number
                }) => {
                  const x = props.x ?? 0
                  const width = Math.max(props.width ?? 0, 2)
                  const midY = (props.y ?? 0) + (props.height ?? 0) / 2
                  const barH = 8
                  return (
                    <rect
                      x={x}
                      y={midY - barH / 2}
                      width={width}
                      height={barH}
                      rx={4}
                      ry={4}
                      className={tone.fill}
                    />
                  )
                }}
              />
            ) : null}

            {value === null ? null : (
              <ReferenceDot
                x={Math.min(max, Math.max(0, value))}
                y={SCALE_ROW}
                r={6}
                strokeWidth={3}
              />
            )}
          </BarChart>
        </ChartContainer>
      </div>

      <BandScaleLabels
        segments={segments}
        activeIndex={activeIndex}
        activeClassName={tone.label}
        onBandSelect={onBandSelect}
      />
    </div>
  )
}


/**
 * The band names under the scale.
 *
 * Outside the chart's `role="img"` wrapper on purpose: when a scale is
 * selectable these are controls, and anything inside that wrapper is
 * read as part of the chart image rather than as something to press.
 */
function BandScaleLabels({
  segments,
  activeIndex,
  activeClassName,
  onBandSelect,
}: {
  segments: readonly { band: Band; width: number }[]
  activeIndex: number
  /** Tone class for the band the current value sits in. */
  activeClassName: string
  onBandSelect?: (band: Band, index: number) => void
}) {
  return (
    <div
      className="mt-1 flex text-xs leading-4"
      style={{ paddingLeft: MARKER_GUTTER, paddingRight: MARKER_GUTTER }}
    >
      {segments.map(({ band, width }, index) => {
        const labelClass = cn(
          "min-w-0 truncate px-0.5 text-center",
          index === activeIndex
            ? cn("font-semibold", activeClassName)
            : "text-muted-foreground",
        )
        if (onBandSelect) {
          return (
            <button
              key={band.label}
              type="button"
              style={{ width: `${width * 100}%` }}
              className={cn(
                labelClass,
                "rounded-1 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
              onClick={() => onBandSelect(band, index)}
            >
              {band.label}
            </button>
          )
        }
        return (
          <span
            key={band.label}
            style={{ width: `${width * 100}%` }}
            className={labelClass}
          >
            {band.label}
          </span>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ coverage */

export type CoverageStatusKey = "under" | "met" | "over"

export interface CoverageStatusSegment {
  key: CoverageStatusKey
  label: string
  count: number
  tone: Band["tone"]
}

/**
 * Inventory rollup for blueprint targets.
 *
 * One headline answers "how done is the paper?"; three equal columns answer
 * not / met / over. Counts live only in the columns — never also as a second
 * sentence next to the title (that read as duplicate noise).
 *
 * Click a column to focus the target tree; click again to clear.
 */
export function CoverageStatusScale({
  segments,
  activeKey,
  onSelect,
}: {
  segments: readonly CoverageStatusSegment[]
  activeKey: CoverageStatusKey | null
  onSelect: (key: CoverageStatusKey | null) => void
}) {
  const total = segments.reduce((sum, s) => sum + s.count, 0)
  const met = segments.find((s) => s.key === "met")?.count ?? 0
  const under = segments.find((s) => s.key === "under")?.count ?? 0
  const over = segments.find((s) => s.key === "over")?.count ?? 0

  const headline =
    total === 0
      ? "No targets"
      : met === total
        ? "Every target met"
        : `${met} of ${total} targets met`

  const dominant =
    under > 0
      ? segments.find((s) => s.key === "under")!
      : over > 0
        ? segments.find((s) => s.key === "over")!
        : segments.find((s) => s.key === "met") ?? segments[0]
  const tone = SCALE_TONE[dominant.tone]

  const summary =
    total === 0
      ? "Coverage: no targets."
      : `Coverage: ${headline}. ${under} not covered, ${met} covered, ${over} over covered.`

  function toggle(key: CoverageStatusKey) {
    onSelect(activeKey === key ? null : key)
  }

  // Bar: share of targets in each state. Empty states keep a hairline so the
  // three-way scale stays readable when one state dominates.
  const EMPTY_SLIVER = 0.06
  const weighted = segments.map((s) => {
    if (total === 0) return 1
    if (s.count > 0) return s.count
    return EMPTY_SLIVER
  })
  const weightTotal = weighted.reduce((a, b) => a + b, 0)

  return (
    <div className="min-w-0 w-full py-1">
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          aria-hidden
          className={cn(
            "grid size-8 shrink-0 place-items-center rounded-full",
            tone.chip
          )}
        >
          <i className="fa-light fa-bullseye-arrow text-sm" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium leading-5 text-foreground">
            Coverage
          </span>
          <span className="text-xs text-muted-foreground">{headline}</span>
        </span>
      </div>

      {/*
        A group, not an image: every child here is a button that filters
        the list. Under `role="img"` their contents were presentational,
        so a screen reader announced the summary and nothing it could
        press.
      */}
      <div role="group" aria-label={summary} className="mt-3 w-full">
        <div className="flex h-2 w-full overflow-hidden rounded-full bg-secondary">
          {segments.map((segment, index) => {
            const width = (weighted[index] / weightTotal) * 100
            const fill = SCALE_TONE[segment.tone]
            const empty = segment.count === 0
            const dimmed =
              empty || (activeKey != null && activeKey !== segment.key)
            return (
              <button
                key={segment.key}
                type="button"
                aria-pressed={activeKey === segment.key}
                aria-label={`${segment.count} ${segment.label}`}
                onClick={() => toggle(segment.key)}
                className={cn(
                  "h-full min-w-0 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  empty ? "bg-muted-foreground/15" : fill.swatch,
                  dimmed && !empty && "opacity-40",
                  index === 0 && "rounded-s-full",
                  index === segments.length - 1 && "rounded-e-full"
                )}
                style={{ width: `${width}%` }}
              />
            )
          })}
        </div>

        <div className="mt-2 grid w-full grid-cols-3 gap-1">
          {segments.map((segment) => {
            const pressed = activeKey === segment.key
            const empty = segment.count === 0
            return (
              <button
                key={segment.key}
                type="button"
                aria-pressed={pressed}
                onClick={() => toggle(segment.key)}
                className={cn(
                  "flex min-w-0 flex-col items-center gap-0.5 rounded-1 px-1 py-1 text-center",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  pressed
                    ? cn("bg-muted/60 font-semibold", SCALE_TONE[segment.tone].label)
                    : empty
                      ? "text-muted-foreground/80"
                      : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                )}
              >
                <span className="font-mono text-sm tabular-nums leading-none">
                  {segment.count}
                </span>
                <span className="min-w-0 max-w-full whitespace-normal text-center text-xs leading-tight">
                  {segment.label}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
